import React, { useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SectionPanel, Field } from "@/views/gooseberry/shared";
import { DESIGN_PHASES } from "@/data/gooseBerryData";

export function MethodAndModePanel({
  methodsAll,
  selectedMethodId,
  setSelectedMethodId,
  selectedMethod,
  onAddCustomMethod,
  language = "en",
}) {
  const t = language === "nl" ? {
    title: "Designmethode",
    help: "Kies de methode die past bij je ontwerp en ontwerpfase.",
    description: "Kies de methode die het beste bij je ontwerp past.",
    method: "Methode",
    methodHint: "Gebruik de methode die het meest relevant is voor de fase van je ontwerp.",
  } : {
    title: "Design method",
    help: "Pick the method that matches your design and design phase.",
    description: "Choose the method that best fits your design.",
    method: "Method",
    methodHint: "Use the method most relevant to your design's stage.",
  };
  return (
    <SectionPanel
      number="2"
      title={t.title}
      required
      help={t.help}
      description={t.description}
      testid="synberry-method-card"
    >
      <Field label={t.method} required testid="synberry-method-select" hint={t.methodHint}>
        <Select value={selectedMethodId} onValueChange={setSelectedMethodId}>
          <SelectTrigger className="rounded-xl" data-testid="synberry-method-trigger"><SelectValue /></SelectTrigger>
          <SelectContent>
            {methodsAll.map((m) => (
              <SelectItem key={m.id} value={m.id}>{m.name} - {m.stage.toLowerCase()}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      {selectedMethod && (
        <div className="mt-3 rounded-xl border border-black/10 bg-white p-3 text-sm text-black/70">
          <div className="text-xs text-[#61CBA3] uppercase tracking-wider">{selectedMethod.stage}</div>
          <div className="font-medium text-black">{selectedMethod.name}</div>
          <div>{selectedMethod.description}</div>
        </div>
      )}
      <AddCustomMethod onAdd={onAddCustomMethod} language={language} />
    </SectionPanel>
  );
}

function AddCustomMethod({ onAdd, language = "en" }) {
  const t = language === "nl" ? {
    required: "Naam van methode is verplicht",
    added: "Eigen methode toegevoegd",
    add: "Eigen methode toevoegen",
    name: "Naam methode",
    description: "Beschrijving",
    recognition: "Herkenningspunten",
    evaluationFocus: "Evaluatiefocus",
    failureModes: "Veelvoorkomende valkuilen (komma-gescheiden)",
    save: "Methode opslaan",
    cancel: "Annuleren",
  } : {
    required: "Method name is required",
    added: "Custom method added",
    add: "Add custom method",
    name: "Method name",
    description: "Description",
    recognition: "Recognition cues",
    evaluationFocus: "Evaluation focus",
    failureModes: "Common failure modes (comma-separated)",
    save: "Save method",
    cancel: "Cancel",
  };
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", stage: "Concept", description: "", recognition: "", evaluationFocus: "", failureModes: "" });
  const submit = () => {
    if (!form.name.trim()) { toast.error(t.required); return; }
    const obj = {
      id: `custom-${Date.now()}`,
      name: form.name.trim(),
      stage: form.stage,
      description: form.description,
      recognition: form.recognition,
      evaluationFocus: form.evaluationFocus,
      failureModes: form.failureModes.split(",").map((x) => x.trim()).filter(Boolean),
    };
    onAdd(obj);
    setOpen(false);
    setForm({ name: "", stage: "Concept", description: "", recognition: "", evaluationFocus: "", failureModes: "" });
    toast(t.added);
  };
  return (
    <div className="mt-3">
      {!open ? (
        <Button variant="outline" className="rounded-xl" onClick={() => setOpen(true)} data-testid="synberry-custom-method-add">
          <Plus className="h-4 w-4 mr-1" /> {t.add}
        </Button>
      ) : (
        <div className="rounded-xl border border-black/10 p-3 space-y-2" data-testid="synberry-custom-method-form">
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t.name} className="rounded-xl" data-testid="synberry-custom-method-name" />
          <div className="flex gap-2">
            <Select value={form.stage} onValueChange={(v) => setForm({ ...form, stage: v })}>
              <SelectTrigger className="rounded-xl w-full" data-testid="synberry-custom-method-stage-trigger"><SelectValue /></SelectTrigger>
              <SelectContent>{DESIGN_PHASES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder={t.description} rows={2} className="rounded-xl" />
          <Input value={form.recognition} onChange={(e) => setForm({ ...form, recognition: e.target.value })} placeholder={t.recognition} className="rounded-xl" />
          <Input value={form.evaluationFocus} onChange={(e) => setForm({ ...form, evaluationFocus: e.target.value })} placeholder={t.evaluationFocus} className="rounded-xl" />
          <Input value={form.failureModes} onChange={(e) => setForm({ ...form, failureModes: e.target.value })} placeholder={t.failureModes} className="rounded-xl" />
          <div className="flex gap-2">
            <Button onClick={submit} className="rounded-xl bg-[#61CBA3] text-white hover:bg-[#4DAF8B]" data-testid="synberry-custom-method-save">{t.save}</Button>
            <Button variant="outline" className="rounded-xl" onClick={() => setOpen(false)}>{t.cancel}</Button>
          </div>
        </div>
      )}
    </div>
  );
}
